import { Box, Center, Loader, Modal, ScrollArea, Stack, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ClientListItem } from 'src/admin/users/types';
import { ReassignAdvisorConfirmModal } from 'src/clients/components/ReassignAdvisorConfirmModal';
import {
  addClientToFamilyPayload,
  clientsOfOtherAdvisers,
} from 'src/clients/CreateFamilyV2/dataHelpers';
import { useFamilies } from 'src/clients/hooks/useFamilies';
import { Family } from 'src/clients/hooks/useFamily';
import { useUpdateFamily } from 'src/clients/hooks/useUpdateFamily';
import { getFamilyMemberClientIds } from 'src/clients/utils';
import { Button } from 'src/design-system/Button';
import { useGlobalConfirmation } from 'src/design-system/GlobalConfirmationProvider';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

type Props = {
  opened: boolean;
  onClose: () => void;
  client: ClientListItem;
};

export function LinkToExistingFamilyModal({ opened, onClose, client }: Props) {
  const navigate = useNavigate();
  const { families, isLoading } = useFamilies();
  const { updateFamily, isLoading: isSaving } = useUpdateFamily();
  const { showConfirmation } = useGlobalConfirmation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [confirmCrossAdvisor, setConfirmCrossAdvisor] = useState(false);

  const clientName = formatName(client.firstName, client.lastName, NameFormat.NATURAL);
  const filteredFamilies = families.filter(family =>
    family.name.toLowerCase().includes(searchTerm.trim().toLowerCase()),
  );

  const conflictingMembers = selectedFamily
    ? clientsOfOtherAdvisers([client], selectedFamily.advisorUserId)
    : [];
  const isCrossAdvisor = conflictingMembers.length > 0;

  function close() {
    setSearchTerm('');
    setSelectedFamily(null);
    setConfirmCrossAdvisor(false);
    onClose();
  }

  async function performLink() {
    if (!selectedFamily) return;
    try {
      await updateFamily(selectedFamily.id, addClientToFamilyPayload(selectedFamily, client.id));
      showConfirmation(`${client.firstName} added to family`);
      navigate(`/family/${selectedFamily.id}/profile?member=${client.id}`);
      close();
    } catch (error) {
      notifyAndLogError(
        'Failed to link to family',
        'An error occurred while linking to the family. Please try again.',
        error,
      );
    }
  }

  function handleLinkPress() {
    if (isCrossAdvisor) {
      setConfirmCrossAdvisor(true);
      return;
    }
    performLink();
  }

  function renderFamilyList() {
    if (isLoading) {
      return (
        <Center py="xl">
          <Loader />
        </Center>
      );
    }
    if (filteredFamilies.length === 0) {
      return (
        <Text c="gray.6" size="sm" ta="center" py="xl">
          {families.length === 0 ? 'No families to link to yet.' : 'No families match your search.'}
        </Text>
      );
    }
    return (
      <ScrollArea.Autosize mah={320}>
        <Stack gap="xs">
          {filteredFamilies.map(family => {
            const isSelected = selectedFamily?.id === family.id;
            const memberCount = getFamilyMemberClientIds(family).length;
            return (
              <Box
                key={family.id}
                component="button"
                type="button"
                onClick={() => setSelectedFamily(family)}
                px="md"
                py="sm"
                style={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 16,
                  backgroundColor: isSelected
                    ? 'var(--mantine-color-brand-0)'
                    : 'var(--mantine-color-gray-0)',
                  border: `2px solid ${isSelected ? 'var(--mantine-color-brand-6)' : 'transparent'}`,
                }}
              >
                <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
                  {family.name}
                </Text>
                <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.7">
                  {memberCount} {memberCount === 1 ? 'member' : 'members'}
                </Text>
              </Box>
            );
          })}
        </Stack>
      </ScrollArea.Autosize>
    );
  }

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        title={`Link ${clientName} to a family`}
        centered
        size={420}
        styles={{
          content: { borderRadius: 24 },
          title: { fontWeight: FONT_WEIGHTS.semibold, fontSize: 'var(--mantine-font-size-lg)' },
        }}
      >
        <Stack gap="md">
          <TextInput
            placeholder="Search families by name"
            value={searchTerm}
            onChange={e => setSearchTerm(e.currentTarget.value)}
            leftSection={<IconSearch size={18} />}
            radius="xl"
          />
          {renderFamilyList()}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            h={48}
            onClick={handleLinkPress}
            loading={isSaving}
            disabled={!selectedFamily}
          >
            Link to family
          </Button>
        </Stack>
      </Modal>
      <ReassignAdvisorConfirmModal
        opened={confirmCrossAdvisor}
        conflictingMembers={conflictingMembers}
        targetAdvisorUserId={selectedFamily?.advisorUserId}
        actionDescription={`Linking them to ${selectedFamily?.name}`}
        confirmLabel="Reassign and link to family"
        isLoading={isSaving}
        onConfirm={performLink}
        onCancel={() => setConfirmCrossAdvisor(false)}
      />
    </>
  );
}
